import { t } from 'i18next'
import { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { usePermissions } from '.'
import {
  permissionActions,
  permissionNames,
  privacyTypes,
} from '../constants/constants'
import {
  dataSourceService,
  notificationService,
  roleService,
} from '../services'
import { RootState } from '../store'
import {
  DataSource,
  DataSourceListResponse,
  setDataSourceDetailsCsv,
  setDataSourceList,
  setDataSourceListCsv,
  setDataSourceListUiFlags,
  setDataSourceLoading,
  setDataSourceTotalResults,
} from '../store/reducers/dataSourceReducer'
import { SearchSource } from '../store/reducers/searchReviewReducer'
import useDownloadFile from './useDownloadFile'

interface UseDataSourcesProps {
  preventFetch?: boolean
}

const useDataSources = ({ preventFetch = false }: UseDataSourcesProps) => {
  const dispatch = useDispatch()
  const loading = useSelector((state: RootState) => state.dataSource.loading)
  const currentUser = useSelector((state: RootState) => state.user.currentUser)
  const downloadCsv = useDownloadFile()

  const {
    selected: selectedDataSource,
    dataSourceList: rawDataSourceList,
    totalResults,
    uiFlags: dataSourceListUiFlag,
  } = useSelector((state: RootState) => state.dataSource)
  const selectedUiFlag = selectedDataSource?.uiFlags

  const { isDeletable, isUpdatable, isCreatable } = usePermissions({
    uiFlag: dataSourceListUiFlag ?? 0,
  })
  const {
    isDeletable: isSelectedDataSourceDeletable,
    isUpdatable: isSelectedDataSourceUpdatable,
    isCreatable: isSelectedDataSourceCreatable,
  } = usePermissions({ uiFlag: selectedUiFlag ?? 0 })

  const dataSourceList: DataSource[] = useMemo(
    () =>
      rawDataSourceList
        .filter((dataSource: DataSource) =>
          currentUser &&
          roleService.hasPermission(
            currentUser,
            permissionNames.DATA_SOURCES,
            permissionActions.ALL
          )
            ? true
            : dataSource.visibility !== privacyTypes.PRIVATE
        )
        .map((dataSource: DataSource) => {
          return {
            ...dataSource,
            key: dataSource.id === undefined ? dataSource.id : dataSource.id,
          }
        }),
    [rawDataSourceList, currentUser]
  )

  const internalDataSourceList = useMemo(
    () => dataSourceList.filter((dataSource) => dataSource.isInternal),
    [dataSourceList]
  )

  const publicDataSourceList = useMemo(
    () => dataSourceList.filter((dataSource) => !dataSource.isInternal),
    [dataSourceList]
  )

  const searchableDataSourceList = useMemo(
    () => dataSourceList.filter((dataSource) => dataSource.searchable),
    [dataSourceList]
  )

  const fetchDataSourceList = useCallback(
    (refreshForceCall = false) => {
      if (!loading && (!preventFetch || refreshForceCall)) {
        dispatch(setDataSourceLoading(true))
        dataSourceService
          .getDataSourceList({})
          .then((response: DataSourceListResponse) => {
            dispatch(setDataSourceLoading(false))
            dispatch(setDataSourceList(response.data))
            dispatch(setDataSourceListUiFlags(response.uiFlags))
            dispatch(setDataSourceTotalResults(response.totalResults))
          })
          .catch((error) => {
            console.error('axios fetch error', error)
            dispatch(setDataSourceLoading(false))
          })
      }
    },
    [dispatch, loading, preventFetch]
  )

  const refreshDataSourceList = useCallback(async () => {
    fetchDataSourceList(true)
  }, [fetchDataSourceList])

  useEffect(() => {
    fetchDataSourceList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const exportDataSourceListCsv = useCallback(() => {
    if (dataSourceList) {
      dataSourceService
        .exportDataSourceList()
        .then((response) => {
          const fileName = 'data_sources.csv'
          downloadCsv(response, fileName)
          dispatch(setDataSourceListCsv(response))
        })
        .catch((error: any) => {
          console.error('Error fetching data source list CSV data', error)
        })
    }
  }, [dataSourceList, dispatch, downloadCsv])

  const exportDataSourceDetailsCsv = useCallback(
    (dataSourceId: string) => {
      if (
        !selectedDataSource ||
        dataSourceId === undefined ||
        dataSourceId === ''
      )
        return

      dataSourceService
        .exportDataSourceDetails({ dataSourceId })
        .then((response) => {
          const fileName = `${selectedDataSource?.title}_${selectedDataSource?.createdDate}.csv`
          downloadCsv(response, fileName)
          dispatch(setDataSourceDetailsCsv(response))
        })
        .catch((error: any) => {
          console.error('Error fetching data source details CSV data', error)
        })
    },
    [downloadCsv, selectedDataSource, dispatch]
  )

  const getDataSourceFiles = useCallback(
    ({ dataSourceId, format }: { dataSourceId: string; format?: string }) => {
      if (!dataSourceId) return

      return dataSourceService
        .getDataSourceFiles({
          dataSourceId,
          format,
        })
        .then((response) => {
          return response
        })
        .catch((error) => {
          console.error('axios get error', error)
          throw error
        })
    },
    []
  )

  const getDataSourceTypeText = useCallback(
    (dataSource: DataSource | string | SearchSource | undefined) => {
      if (typeof dataSource === 'string') {
        return dataSource
      } else if (dataSource) {
        if (Object.keys(dataSource).length === 0) {
          return ''
        }
        return `${dataSource.typeCode} : ${dataSource.title}`
      }
      return ''
    },
    []
  )

  interface GetLatestIterationOfFileProps {
    dataSourceId: string
    documentId: string
  }

  interface LatestIterationResponse {
    id: string
    path: string
    webUrl: string
  }

  const getLatestIterationOfFile = useCallback(
    async ({ dataSourceId, documentId }: GetLatestIterationOfFileProps) => {
      try {
        const response: LatestIterationResponse =
          await dataSourceService.getLatestIteration({
            dataSourceId,
            documentId,
          })
        return response.path
      } catch (error) {
        notificationService.notificationError({
          messageToDisplay: t(
            'workspaceDetailsPage.conformityCheckSmartFunction.latestIterationError'
          ),
        })
        return null
      }
    },
    []
  )

  interface GetPermittedUsersOfDataSourceProps {
    dataSourceId: string
  }

  const getPermittedUsersOfDataSource = useCallback(
    async ({ dataSourceId }: GetPermittedUsersOfDataSourceProps) => {
      return dataSourceService.getPermittedUsersOfDataSource({ dataSourceId })
    },
    []
  )

  const fetchDataSourceDetails = useCallback(async (dataSourceId: string) => {
    try {
      const response = await dataSourceService.getDataSourceDetails({
        dataSourceId,
      })
      return response
    } catch (error) {
      console.error('axios fetch error', error)
      throw error
    }
  }, [])

  return {
    dataSourceList,
    exportDataSourceDetailsCsv,
    exportDataSourceListCsv,
    internalDataSourceList,
    loading,
    publicDataSourceList,
    searchableDataSourceList,
    refreshDataSourceList,
    selectedDataSource,
    getDataSourceFiles,
    getDataSourceTypeText,
    getLatestIterationOfFile,
    totalResults,
    dataSourceListUiFlag,
    isDeletable,
    isUpdatable,
    isCreatable,
    isSelectedDataSourceDeletable,
    isSelectedDataSourceUpdatable,
    isSelectedDataSourceCreatable,
    getPermittedUsersOfDataSource,
    fetchDataSourceDetails,
  }
}

export default useDataSources
